import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { fetchInbox, ApiError, type InboxLite, type SendResult } from "../../api";
import { EmailDetailModal } from "../cockpit/EmailDetailModal";
import { EmailComposeModal } from "./EmailComposeModal";
import { EmailListRow } from "./EmailListRow";

type Filter = "all" | "incoming" | "outgoing";

/**
 * EM-6 — the EMAIL feature as a cockpit panel: the WHOLE inbox (Inbox+Sent,
 * Homami-walled, unfiltered, searchable + paginated via fetchInbox), rendered in
 * the FOCUS-mode look (EmailListRow). Not deal-scoped — opening a row resolves
 * that email's linked deal for the From-docs / Save-to-WorkDrive features (the
 * detail modal does this; unlinked mail falls back to its pick-a-deal flow).
 *
 * Self-contained (no modal shell of its own) so it can sit inline in the cockpit
 * after the layout swap; for now it's hosted inside EmailWorkspaceModal.
 */
export function CockpitEmailPanel() {
  const [emails, setEmails] = useState<InboxLite[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [sent, setSent] = useState<SendResult | null>(null);
  // Bumped per search so a stale in-flight response can't overwrite a newer one.
  const reqRef = useRef(0);

  const runSearch = useCallback(async (q: string) => {
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const page = await fetchInbox({ search: q || undefined, limit: 50 });
      if (reqRef.current !== reqId) return; // superseded
      setEmails(page.emails);
      setCursor(page.nextCursor);
    } catch (err) {
      if (reqRef.current !== reqId) return;
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "fetch_failed",
      );
    } finally {
      if (reqRef.current === reqId) setLoading(false);
    }
  }, []);

  // Debounced server search (also the initial load with q="").
  useEffect(() => {
    const h = setTimeout(() => void runSearch(search.trim()), search ? 300 : 0);
    return () => clearTimeout(h);
  }, [search, runSearch]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchInbox({ search: search.trim() || undefined, cursor });
      setEmails((prev) => [...prev, ...page.emails]);
      setCursor(page.nextCursor);
    } catch {
      /* keep what we have; user can retry */
    } finally {
      setLoadingMore(false);
    }
  };

  // Client-side direction filter on the loaded page (server handles search).
  const filtered = useMemo(
    () => (filter === "all" ? emails : emails.filter((e) => e.direction === filter)),
    [emails, filter],
  );

  return (
    <div className="email-tab">
      <div className="email-header">
        <div className="email-header-counts">
          <span className="email-header-total">{emails.length} EMAILS</span>
          <span className="email-header-meta"> · whole inbox</span>
        </div>
        <div className="email-header-actions">
          <button className="email-compose-btn" onClick={() => setComposing(true)}>
            ✎ NEW EMAIL
          </button>
        </div>
      </div>

      {sent && (
        <div className="email-sent-banner">
          ✓ Email sent
          {sent.redirected
            ? ` — redirected to ${sent.sent_to.join(", ")} by the send guard. Intended: ${sent.intended_to.join(", ")}.`
            : ` to ${sent.sent_to.join(", ")}.`}
        </div>
      )}

      <div className="email-controls">
        <div className="email-filter-pills">
          {(["all", "incoming", "outgoing"] as Filter[]).map((f) => (
            <button
              key={f}
              className={clsx("email-filter-pill", filter === f && "active")}
              onClick={() => setFilter(f)}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        <input
          className="email-search"
          type="search"
          placeholder="Search your whole inbox…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="email-list">
        {loading ? (
          <div className="email-detail-loading">
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-label">LOADING INBOX</span>
          </div>
        ) : error ? (
          <div className="lme-error">
            ⚠ {error}
            <button className="lme-retry" onClick={() => void runSearch(search.trim())}>
              RETRY
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="email-empty">
            {search.trim()
              ? `No matches for “${search.trim()}”.`
              : filter !== "all"
                ? "No emails match this filter."
                : "Inbox is empty."}
          </div>
        ) : (
          <>
            {filtered.map((e) => (
              <EmailListRow key={e.id} email={e} onClick={() => setSelectedId(e.id)} />
            ))}
            {filter === "all" && cursor && (
              <button
                className="activity-expand-btn"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? "LOADING…" : "▾ LOAD MORE"}
              </button>
            )}
          </>
        )}
      </div>

      {selectedId && (
        <EmailDetailModal
          messageId={selectedId}
          // No explicit deal — the modal resolves this email's linked deal (and
          // lets you pick one if unlinked) to scope From-docs / Save-to-WorkDrive.
          onClose={() => setSelectedId(null)}
          onMutated={() => void runSearch(search.trim())}
        />
      )}

      {composing && (
        <EmailComposeModal
          onClose={() => setComposing(false)}
          onSent={(result) => {
            setSent(result);
            setComposing(false);
          }}
        />
      )}
    </div>
  );
}
